import type { EmploymentStatus, HRInterviewResults } from '@/types/hrInterview'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { SelectorDeResidencia } from '@/features/rh/components/SelectorDeResidencia'
import {
  ESTATUS_LABORAL,
  etiquetaDeEstatus,
  formatoDeSueldo,
} from '@/features/rh/lib/datosDelCandidato'

/**
 * Los cinco datos del candidato que RH captura despues de la entrevista (B2).
 *
 * Captura y consulta viven en el mismo componente a proposito: si fueran dos,
 * la etiqueta de un campo se podria corregir en uno y quedarse vieja en el
 * otro, y el mismo dato se leeria distinto segun la pantalla.
 *
 * Sale aparte de CandidateDetailView, que pasa de dos mil lineas.
 */

const claseInput =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
const claseLabel = 'block text-sm font-medium text-gray-700 mb-1'

export const DatosDelCandidato = ({
  valores,
  onChange,
}: {
  valores: HRInterviewResults
  onChange: (campo: keyof HRInterviewResults, valor: string | number | null) => void
}) => {
  /**
   * El input vacio manda null, no 0.
   *
   * Con 0 se guardaria «gana cero» en vez de «no lo dijo», y despues no habria
   * forma de distinguirlos.
   */
  const aNumero = (texto: string): number | null =>
    texto.trim() === '' ? null : Number(texto)

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className={claseLabel}>Último salario mensual bruto</label>
        <input
          type="number"
          min={0}
          step="0.01"
          value={valores.previousMonthlySalary ?? ''}
          onChange={(e) => onChange('previousMonthlySalary', aNumero(e.target.value))}
          className={claseInput}
          placeholder="Ej: 18500.00"
        />
      </div>

      <div>
        <label className={claseLabel}>Expectativa salarial</label>
        <input
          type="number"
          min={0}
          step="0.01"
          value={valores.salaryExpectation ?? ''}
          onChange={(e) => onChange('salaryExpectation', aNumero(e.target.value))}
          className={claseInput}
          placeholder="Ej: 22000.00"
        />
      </div>

      <div>
        <label className={claseLabel}>Estatus laboral</label>
        <SelectBuscable
          variante="formulario"
          value={valores.employmentStatus ?? ''}
          onChange={(valor) => onChange('employmentStatus', valor || null)}
          placeholder="Sin especificar"
          placeholderDeBusqueda="Buscar estatus..."
          opciones={[
            { value: '', label: 'Sin especificar' },
            ...ESTATUS_LABORAL.map((opcion) => ({
              value: opcion.value,
              label: opcion.label,
            })),
          ]}
        />
      </div>

      <div>
        <label className={claseLabel}>Disponibilidad de incorporación</label>
        <input
          type="text"
          maxLength={160}
          value={valores.availability ?? ''}
          onChange={(e) => onChange('availability', e.target.value)}
          className={claseInput}
          placeholder="Ej: Inmediata, o a partir del 15 de octubre"
        />
      </div>

      <SelectorDeResidencia
        valor={valores.residenceCity ?? ''}
        onChange={(texto) => onChange('residenceCity', texto)}
        idBase="entrevista-residencia"
        etiqueta={(htmlFor, texto) => (
          <label htmlFor={htmlFor} className={claseLabel}>
            {texto}
          </label>
        )}
        claseInput={claseInput}
      />
    </div>
  )
}

/** Los mismos cinco datos, ya guardados. */
export const DatosDelCandidatoEnConsulta = ({
  datos,
}: {
  /**
   * Los importes se aceptan como cadena porque asi llegan del servidor. Aqui
   * solo se formatean, nunca se opera con ellos.
   */
  datos: {
    previousMonthlySalary?: number | string | null
    salaryExpectation?: number | string | null
    employmentStatus?: EmploymentStatus | null
    availability?: string
    residenceCity?: string
  }
}) => {
  const renglones = [
    { etiqueta: 'Último salario mensual bruto', valor: formatoDeSueldo(datos.previousMonthlySalary) },
    { etiqueta: 'Expectativa salarial', valor: formatoDeSueldo(datos.salaryExpectation) },
    { etiqueta: 'Estatus laboral', valor: etiquetaDeEstatus(datos.employmentStatus) },
    { etiqueta: 'Disponibilidad', valor: datos.availability?.trim() || '—' },
    { etiqueta: 'Ciudad de residencia', valor: datos.residenceCity?.trim() || '—' },
  ]

  // Si no se capturo ninguno, no se pinta una tabla de guiones.
  if (renglones.every((r) => r.valor === '—' || r.valor === 'No especificado')) return null

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
      {renglones.map((r) => (
        <div key={r.etiqueta}>
          <p className="text-xs text-gray-500">{r.etiqueta}</p>
          <p className="text-sm text-gray-900 font-medium">{r.valor}</p>
        </div>
      ))}
    </div>
  )
}
